/***************************************************************************************
 * FileName      : EquipmentModal.tsx
 * Description   : 장비 학습하기 모달 (Sweep 이동 완료 후 표시)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.09
 * Modified Date : 2026.02.13
 * Reference     : mockup/index.html (EQUIPMENT DETAIL POPUP), mockup/asset/js/main.js, doc/equipment-description/
 ***************************************************************************************/

import React, { useState } from 'react';
// 2026.02.09 : 박은정 : 학습카드 정의 + 카드 진행 데이터 타입 임포트
import { EQUIPMENT_CARDS, CardProgress } from '../services/LearningProgressService';

// 2026.02.09 : 박은정 : 장비 스펙 항목 타입
interface EquipmentSpec {
  label: string;
  value: string;
}

// 2026.02.09 : 박은정 : 장비 데이터 타입
export interface EquipmentInfo {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  specs: EquipmentSpec[];
}

// 2026.02.09 : 박은정 : 장비별 상세 데이터 (mockup/asset/js/main.js EQUIPMENT_DATA 기준)
// 2026.02.12 : 박은정 : 장비 설명·스펙을 doc/equipment-description 기준으로 업데이트
export const EQUIPMENT_DATA: Record<string, EquipmentInfo> = {
  'heat-exchanger': {
    id: 'heat-exchanger',
    name: '열교환기',
    nameEn: 'Heat Exchanger System',
    description:
      '고온·저온 유체 간 열 교환 현상을 실제 장치에서 관찰하고, 온도·유량·압력 데이터로 열전달 이론(LMTD, ε-NTU)을 검증합니다. 판형(PH-103), 쉘앤튜브(HE-104), 침지식 코일(CH-107) 3종 열교환기를 비교·분석합니다.',
    specs: [
      { label: '주요 기기', value: 'PH-103, HE-104, CH-107' },
      { label: '칠러 온도', value: '-5 ~ 10°C' },
      { label: '인버터', value: '0 ~ 60Hz' },
    ],
  },
  distillation: {
    id: 'distillation',
    name: '증류기',
    nameEn: 'Distillation Lab',
    description:
      '끓는점 차이를 이용한 분별증류 원리를 4단 유리 증류탑(DT-203)에서 직접 관찰합니다. 에탄올(78.3℃)과 증류수(100℃) 혼합용액에서 고순도 에탄올을 분리하는 전체 과정을 체험합니다.',
    specs: [
      { label: '주요 기기', value: 'TK-201, DT-203, HE-204, KO-205' },
      { label: '히터 온도', value: '80 ~ 90°C (On-Off)' },
      { label: '칠러', value: '-5 ~ 10°C' },
    ],
  },
  circulation: {
    id: 'circulation',
    name: '순환기',
    nameEn: 'Circulation System',
    description:
      'EDUCTOR 교반(1계열)과 BUBBLE 교반(2계열) 2개의 독립 계열로 구성된 혼합/교반 시스템입니다. 용매(물)와 프로세스 가스(공기)의 혼합 및 용존산소(DO) 변화를 시각적으로 관찰합니다.',
    specs: [
      { label: '주요 기기', value: 'PP-302, BL-304, ED-303' },
      { label: '인버터', value: '0 ~ 60Hz' },
      { label: '용존산소', value: '6 ~ 9+ ppm' },
    ],
  },
};

interface Props {
  isVisible: boolean;
  equipmentId: string | null;
  onClose: () => void;
  // 2026.02.09 : 박은정 : 학습하기 콜백 - 선택한 카드 ID도 전달
  onStartLearning: (equipmentId: string, cardId?: string) => void;
  // 2026.02.09 : 박은정 : 학습 진행률(%) 및 카드별 상태 (localStorage 연동)
  progress?: number;
  cardStatuses?: Record<string, CardProgress>;
}

// 2026.02.09 : 박은정 : 장비 학습하기 모달 컴포넌트 (진행률 + 카드 체크리스트 포함)
export const EquipmentModal: React.FC<Props> = ({
  isVisible,
  equipmentId,
  onClose,
  onStartLearning,
  progress = 0,
  cardStatuses = {},
}) => {
  // 2026.02.09 : 박은정 : 모달 내 선택된 학습카드 ID 상태 (클릭으로 진도 선택)
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  if (!isVisible || !equipmentId) return null;

  const equipment = EQUIPMENT_DATA[equipmentId];
  if (!equipment) return null;

  const cards = EQUIPMENT_CARDS[equipmentId] || [];

  // 2026.02.09 : 박은정 : "이어서 학습하기" 대상 — 진행중 카드 또는 첫 미완료 카드
  const resumeCardId =
    cards.find((c) => cardStatuses[c.id]?.status === 'in-progress')?.id
    || cards.find((c) => cardStatuses[c.id]?.status !== 'completed')?.id
    || cards[0]?.id;

  // 2026.02.09 : 박은정 : "학습하기" 대상 — 사이드바에서 선택한 카드 (미선택 시 첫 카드)
  // 2026.02.13 : 박은정 : 미선택 시 이어서 학습 대상 카드로 변경 (자동 선택된 카드부터 이어서 학습)
  const selectedStartCardId = selectedCardId || resumeCardId;

  // 2026.02.09 : 박은정 : 사이드바 체크리스트의 선택 표시용 (선택한 카드 우선, 없으면 이어서 학습 대상)
  const highlightedCardId = selectedCardId || resumeCardId;

  // 2026.02.09 : 박은정 : 이어서 학습하기 버튼 표시 조건 (진행 중인 학습이 있을 때만)
  const showResumeBtn = progress > 0 && progress < 100;

  return (
    <div className="eq-modal eq-modal--visible">
      {/* 배경 클릭 시 닫기 */}
      <div className="eq-modal__backdrop" onClick={onClose} />

      {/* 2026.02.09 : 박은정 : 모달(중앙) + 사이드바(오른쪽) 레이아웃 래퍼 */}
      <div className="eq-modal__layout">
        {/* 모달 카드 (장비 정보) */}
        <div className="eq-modal__card">
          {/* 헤더 */}
          <div className="eq-modal__header">
            <div className="eq-modal__header-text">
              <h2 className="eq-modal__name">{equipment.name}</h2>
              <span className="eq-modal__name-en">{equipment.nameEn}</span>
            </div>
            <button className="eq-modal__close" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* 본문 */}
          <div className="eq-modal__body">
            <p className="eq-modal__desc">{equipment.description}</p>

            {/* 스펙 테이블 */}
            <div className="eq-modal__specs">
              {equipment.specs.map((spec, idx) => (
                <div className="eq-modal__spec-row" key={idx}>
                  <span className="eq-modal__spec-label">{spec.label}</span>
                  <span className="eq-modal__spec-value">{spec.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 2026.02.09 : 박은정 : 푸터 — 이어서 학습하기 / 학습하기 버튼 분리 */}
          <div className={`eq-modal__footer${showResumeBtn ? ' eq-modal__footer--dual' : ''}`}>
            {/* 이어서 학습하기: 진행률 > 0 && < 100 일 때만 표시, 진행중 카드부터 시작 */}
            {showResumeBtn && (
              <button
                className="eq-modal__learn-btn eq-modal__learn-btn--resume"
                onClick={() => onStartLearning(equipmentId, resumeCardId || undefined)}
              >
                이어서 학습하기
              </button>
            )}
            {/* 학습하기: 사이드바에서 선택한 카드부터 시작 (선택 없으면 첫 카드) */}
            <button
              className="eq-modal__learn-btn eq-modal__learn-btn--start"
              onClick={() => onStartLearning(equipmentId, selectedStartCardId || undefined)}
            >
              {progress >= 100 ? '다시 학습하기' : '학습하기'}
            </button>
          </div>
        </div>

        {/* 2026.02.09 : 박은정 : 학습현황 사이드바 패널 (모달 오른쪽) */}
        <div className="eq-modal__sidebar">
          <div className="eq-modal__sidebar-header">
            <span className="eq-modal__sidebar-title">학습현황</span>
          </div>

          {/* 진행률 바 */}
          <div className="eq-modal__progress">
            <div className="eq-modal__progress-header">
              <span className="eq-modal__progress-title">진행률</span>
              <span className="eq-modal__progress-value">{progress}%</span>
            </div>
            <div className="eq-modal__progress-bar">
              <div
                className="eq-modal__progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* 학습카드 체크리스트 (클릭하여 진도 선택 가능) */}
          {cards.length > 0 && (
            <div className="eq-modal__checklist">
              <span className="eq-modal__checklist-hint">학습할 진도를 선택하세요</span>
              <div className="eq-modal__checklist-items">
                {cards.map((card, idx) => {
                  const status = cardStatuses[card.id]?.status || 'pending';
                  const isSelected = highlightedCardId === card.id;
                  return (
                    <button
                      key={card.id}
                      className={
                        `eq-modal__check-item eq-modal__check-item--${status}`
                        + (isSelected ? ' eq-modal__check-item--selected' : '')
                      }
                      onClick={() => setSelectedCardId(card.id)}
                      type="button"
                    >
                      <span className="eq-modal__check-num">{idx + 1}</span>
                      <span className="eq-modal__check-label">{card.label}</span>
                      <span className="eq-modal__check-icon">
                        {status === 'completed' ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : status === 'in-progress' ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="4" fill="currentColor"/>
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

